import React, { useEffect, useState } from 'react';
import { CheckCircle2, Loader2, AlertCircle } from 'lucide-react';
import { Progress } from './ui/progress';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';

export function ProgressIndicator({ title, steps, currentStep, showDetails = false, onComplete }) {
  const [progress, setProgress] = useState(0);

  const completedSteps = steps.filter((step) => step.status === 'completed').length;
  const totalSteps = steps.length || 1;

  useEffect(() => {
    const newProgress = (completedSteps / totalSteps) * 100;
    setProgress(newProgress);

    if (newProgress === 100 && typeof onComplete === 'function') {
      const timeout = setTimeout(() => onComplete(), 500);
      return () => clearTimeout(timeout);
    }

    return undefined;
  }, [completedSteps, totalSteps, onComplete]);

  const getStepIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle2 className="w-4 h-4 text-green-500" />;
      case 'in-progress':
        return <Loader2 className="w-4 h-4 text-accent animate-spin" />;
      case 'error':
        return <AlertCircle className="w-4 h-4 text-red-500" />;
      default:
        return <div className="w-4 h-4 rounded-full border-2 border-muted" />;
    }
  };

  const getStepStyles = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-green-50 border-green-200 dark:bg-green-950/20 dark:border-green-800';
      case 'in-progress':
        return 'bg-accent/10 border-accent/30 animate-pulse';
      case 'error':
        return 'bg-red-50 border-red-200 dark:bg-red-950/20 dark:border-red-800';
      default:
        return 'bg-muted/30 border-muted';
    }
  };

  return (
    <Card className="w-full max-w-md mx-auto">
      <CardContent className="p-6 space-y-4">
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold">{title}</h3>
            <Badge variant="secondary" className="font-mono text-xs">{completedSteps}/{totalSteps}</Badge>
          </div>
          <Progress value={progress} className="h-2" />
          <p className="text-sm text-muted-foreground">
            {progress === 100 ? 'Complete!' : `${Math.round(progress)}% complete`}
          </p>
        </div>

        {showDetails && (
          <div className="space-y-3">
            {steps.map((step) => (
              <div key={step.id} className={`flex items-start gap-3 p-3 rounded-lg border transition-all ${getStepStyles(step.status)}`}>
                <div className="flex-shrink-0 mt-0.5">{getStepIcon(step.status)}</div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <p className="font-medium text-sm">{step.label}</p>
                    {step.status === 'in-progress' && currentStep === step.id && (
                      <Badge variant="secondary" className="text-xs">Current</Badge>
                    )}
                  </div>
                  {step.description && (
                    <p className="text-xs text-muted-foreground mt-1">{step.description}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
